import { useState } from 'react';
import styled from '@emotion/styled';
import { SxProps } from '@mui/material';
import { MenuIcon } from 'src/icons';
import { mq } from 'src/themes/mediaQueries';
import { IconButton, Menu, MenuItem, SvgIcon, Typography } from 'src/components/atoms';
import { Flex } from 'src/components/layout';

export interface Option {
  icon: JSX.Element;
  text: string;
  color?: string;
  disabled?: boolean;
  onClick?: () => void;
}

interface Props {
  options: Option[];
  sx?: SxProps;
}

const StyledOptionsButton = styled(IconButton)`
  position: relative;
  z-index: 10;

  ${mq.xs} {
    svg {
      height: 0.825rem;
      width: 0.825rem;
    }
  }
`;

export const MoreOptionsMenu = ({ options, sx }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectOption = (e: React.MouseEvent<HTMLLIElement>, option: Option) => {
    e.stopPropagation();

    if (option.onClick) {
      option.onClick();
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <StyledOptionsButton onClick={handleOpenMenu} data-testid='more options menu' sx={sx}>
        <SvgIcon fontSize='small'>
          <MenuIcon />
        </SvgIcon>
      </StyledOptionsButton>
      <Menu
        id='basic-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        sx={{ ml: -3 }}
      >
        {options.map(option => (
          <MenuItem
            value={option.text}
            key={option.text}
            onClick={e => handleSelectOption(e, option)}
            disabled={option.disabled}
          >
            <Flex p={0.25}>
              <SvgIcon htmlColor={option.color}>{option.icon}</SvgIcon>
              <Typography variant='body2' sx={{ ml: 1, color: option.color }}>
                {option.text}
              </Typography>
            </Flex>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
